import { Box } from '@everlywell/ui-kit';
import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import StatsBar from 'components/StatsBar';
import { graphql } from 'gatsby';
import React, { lazy, LazyExoticComponent, useEffect } from 'react';
import { IContentWithImage } from 'utils/types';

import { SEO } from '../components/SEO';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';

const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);

const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const ProductCardGridSection = lazy(() => import('components/ProductCardGrid'));
const StepCardListSection = lazy(() => import('components/StepCardList'));
const StatsBarSection = lazy(() => import('components/StatsBar'));

type Props = {
  data: {
    partnersContent: {
      title: string;
      heroSection: {
        headline: string;
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        componentName: string;
        headline: string;
        backgroundColor: string;
        disclaimer?: {
          disclaimer: string;
        };
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container: any;
      }[];
    };
  };
};

const components: {
  HeroSection: React.FC<any>;
  MembersSatisfaction: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
  ProductCardGrid: LazyExoticComponent<React.FC<any>>;
  StepCardList: LazyExoticComponent<React.FC<any>>;
  StatsSection: LazyExoticComponent<React.FC<any>>;
} = {
  HeroSection,
  MembersSatisfaction,
  HowItWorksSection: HowItWorksSection,
  ProductCardGrid: ProductCardGridSection,
  StepCardList: StepCardListSection,
  StatsSection: StatsBarSection,
};

const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStepCardList = (name: string): boolean => name === 'StepCardList';
const isStatsBar = (name: string): boolean => name === 'StatsSection';
export const Head = () => <SEO />;

const PartnersPage: React.FC<Props> = ({ data }) => {
  // @ts-ignore
  const { hero, sections } = data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Partners page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['partnersContent']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (isProductCardGrid(section.componentName)) {
            return (
              <ProductCardGrid
                key={`${section.componentName}-${i}`}
                {...section}
                title={section.title ?? ''}
                cards={section.container}
                sectionHeader={{
                  ...section,
                  eyebrow: section.title || '',
                  title: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody,
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            );
          }

          if (isStatsBar(section.componentName)) {
            return (
              <Box
                backgroundColor={section.backgroundColor}
                pt={['4rem', '6rem', '6rem']}
                pb="0"
              >
                <StatsBar
                  key={`${section.componentName}-${i}`}
                  stats={section.container[0]?.statsCollection}
                  disclaimer={{
                    childMarkdownRemark: {
                      rawMarkdownBody: section.disclaimer?.disclaimer || '',
                      html: section.disclaimer?.disclaimer || '',
                    },
                  }}
                  backgroundColor={section.container[0]?.backgroundColor}
                  textColor={section.container[0]?.textColor}
                />
              </Box>
            );
          }

          if (isStepCardList(section.componentName)) {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            );
          }

          return (
            // @ts-ignore
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default PartnersPage;

export const data = graphql`
  query PartnersPageQuery {
    contentfulPage001(displayName: { eq: "Partners Page | EHS" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          eyebrowColor
          headline
          backgroundColor
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          title
          url
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
              }
            }
            image {
              url
            }
          }
        }
        container {
          ... on ContentfulStatsBar {
            backgroundColor
            textColor
            statsCollection {
              suffix
              label
              value
              prefix
            }
          }
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
            description
            link
          }
        }
      }
    }
  }
`;
